import { uiConfig } from '@/config';
import { cn } from '@/libs';
import { FieldError } from '../../core';
import { FormFieldType, SingleFormField } from '../../typings';
import { Checkbox } from '../inputs';
import {
  useClearOnNotApplicable,
  useDisabledWhenToggled,
  useFieldWarning,
  useNotApplicableField,
  useShouldValidateField,
} from '../../hooks';
import { FormFieldMapper as AnyFormFieldMapper } from '..';

import { SingleFieldInnerPresenter } from './single-field';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';
import { FieldWarningAlert } from './FieldWarningAlert';

import './SingleFieldPresenter.scss';

type SingleFieldPresenterProps = {
  field: SingleFormField;
  disabled?: boolean;
};

/**
 * @internal
 * This component is for internal use only.
 *
 * SingleFieldMapper is a functional component that takes field properties and
 * returns the appropriate form field based on the field type.
 */
export const SingleFieldPresenter: React.FC<SingleFieldPresenterProps> = ({
  field,
  disabled,
}) => {
  const containerClasses = field.size
    ? cn(
        'sf__container',
        uiConfig.grid.maxColumnsSpanClass,
        { 'sf__container--checkbox': field.type === FormFieldType.Checkbox },
        Object.entries(field.size).map(([key, value]) => ({
          [`${key}:col-span-${value}`]: value !== undefined,
        })),
      )
    : undefined;

  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);

  // Manage shouldValidate state for fields with NA checkbox
  useShouldValidateField(field.fieldId, !isFieldNA);

  useClearOnNotApplicable(field, isFieldNA);

  const isDisabledByToggle = useDisabledWhenToggled(field);

  const warning = useFieldWarning(field);

  if ('hidden' in field && field.hidden) {
    return null;
  }

  return (
    <div className={containerClasses}>
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}
      <SingleFieldInnerPresenter
        field={field}
        disabled={isFieldNA || disabled || isDisabledByToggle}
      />

      {/* Group restriction guarding this field — shown here, not at group level. */}
      {field.restrictionErrorKey && (
        <FieldError name={field.restrictionErrorKey} />
      )}

      {field.notApplicableField !== undefined && (
        <Checkbox
          name={field.notApplicableField.fieldId}
          label={field.notApplicableField.label}
          size="small"
        />
      )}

      {isFieldNA && field.naDetails && (
        <AnyFormFieldMapper field={field.naDetails} disabled={disabled} />
      )}

      {/* Last child, so the notice sits under the whole field rather than between the
          input and the Not Applicable checkbox that belongs beside it. */}
      {warning && !isFieldNA && <FieldWarningAlert warning={warning} />}
    </div>
  );
};
